import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrambleText } from '../components/ui/ScrambleText';

const TeamMemberCard = ({ name, title, image, delay, hasButton = true, profileUrl = "#", links = {} }) => {
    const [hovered, setHovered] = useState(false);
    
    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay }}
            className="text-center flex flex-col h-full relative group p-6 rounded-xl"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <AnimatePresence>
                {hovered && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-gradient-to-b from-[#4F6A63]/10 to-[#4F6A63]/5 rounded-xl border border-[#4F6A63]/10"
                        style={{ backdropFilter: 'blur(8px)' }}
                    />
                )}
            </AnimatePresence>
            
            <div className="flex-grow relative z-10 flex flex-col items-center">
                <div className="mb-6">
                    <img 
                        className="relative w-40 h-40 rounded-full transition-all duration-300 group-hover:scale-105" 
                        src={image} 
                        alt={`${name} Avatar`} 
                    />
                </div>
                <div className="space-y-3 mb-6">
                    <h3 className="text-2xl font-bold tracking-tight font-mono text-[#DBDBDB]">
                        <ScrambleText text={name} delay={delay * 1000} />
                    </h3>
                    <p className="text-[#4F6A63] font-mono text-sm">
                        <ScrambleText text={title} delay={(delay * 1000) + 50} />
                    </p>
                </div>
            </div>

            <div className="relative z-10 space-y-3">
                {(links.scholar || links.linkedin) && (
                    <div className="flex justify-center gap-2">
                        {links.scholar && (
                            <a 
                                href={links.scholar}
                                target="_blank"
                                rel="noopener noreferrer" 
                                className="inline-block px-3 py-1 text-xs font-medium text-[#4F6A63] border border-[#4F6A63] rounded-lg hover:bg-[#4F6A63]/10 transition-all duration-300"
                            >
                                Scholar
                            </a>
                        )}
                        {links.linkedin && (
                            <a 
                                href={links.linkedin}
                                target="_blank"
                                rel="noopener noreferrer" 
                                className="inline-block px-3 py-1 text-xs font-medium text-[#4F6A63] border border-[#4F6A63] rounded-lg hover:bg-[#4F6A63]/10 transition-all duration-300"
                            >
                                LinkedIn
                            </a>
                        )}
                    </div>
                )}
                {hasButton && (
                    <div>
                        <a 
                            href={profileUrl}
                            className="inline-block px-4 py-1.5 text-xs font-medium text-[#4F6A63] border border-[#4F6A63] rounded-lg hover:bg-[#4F6A63]/10 transition-all duration-300 group-hover:border-[#4F6A63]/80"
                        >
                            View Articles
                        </a>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default function Team() {
    const teamMembers = [
        { 
            name: "Dave Lemphers", 
            title: "Chief Maincoder", 
            image: "/team/dave.png", 
            delay: 0.4,
            profileUrl: "#",
            links: {
                linkedin: "https://linkedin.com/in/davelemphers",
            }
        },
        { 
            name: "Lukas Wesemann", 
            title: "AI Research and Product", 
            image: "/team/lukas.png", 
            delay: 0.5,
            profileUrl: "https://blog.maincode.com/author/lukas/",
            links: {
                scholar: "https://scholar.google.com/citations?user=YOUR_ID",
                linkedin: "https://linkedin.com/in/lukaswesemann"
            }
        },
        { 
            name: "Fabian Waschkowski", 
            title: "AI research engineering", 
            image: "/team/fabian.png", 
            delay: 0.6,
            profileUrl: "#",
            links: {
                scholar: "https://scholar.google.com/citations?user=YOUR_ID",
                linkedin: "https://linkedin.com/in/fabianwaschkowski"
            }
        },
        { 
            name: "Kees Bakker", 
            title: "Design Engineer", 
            image: "/team/kees.png", 
            delay: 0.7,
            profileUrl: "#",
            links: {
                linkedin: "https://linkedin.com/in/keesbakker"
            }
        },
        { 
            name: "Carla Budidharma", 
            title: "Executive Assistant", 
            image: "/team/carla.png", 
            delay: 0.8, 
            hasButton: false,
            profileUrl: "#",
            links: {
                linkedin: "https://linkedin.com/in/carlabudidharma"
            }
        }
    ];

    return (
        <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#0A0A0A] text-white py-24 md:py-32 relative min-h-screen"
        >
            <div className="absolute inset-0 bottom-0 left-0 right-0 top-0 bg-[linear-gradient(to_right,#27272733_1px,transparent_1px),linear-gradient(to_bottom,#27272733_1px,transparent_1px)] bg-[size:84px_84px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
            <div className="py-8 px-4 mx-auto max-w-screen-xl text-center lg:py-16 lg:px-6 relative z-10">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mx-auto mb-16 max-w-screen-sm lg:mb-20"
                >
                    <h2 className="mb-6 text-5xl tracking-tight font-extrabold font-mono">
                        <ScrambleText text="Meet the Maincoders" className="text-[#DBDBDB]" />
                    </h2>
                    <p className="font-light text-[#4F6A63] sm:text-xl font-mono max-w-2xl mx-auto">
                        <ScrambleText
                            text="Yada yada yada"
                            delay={200}
                        />
                    </p>
                </motion.div>
                
                <div className="grid gap-8 lg:gap-12 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {teamMembers.map((member) => (
                        <TeamMemberCard key={member.name} {...member} />
                    ))}
                </div>
            </div>
        </motion.section>
    );
} 